// ts for mux-embed
/// <reference path="../../../../node_modules/mux-embed/dist/types/mux-embed.d.ts"/>
import {
  AppNameEnum,
  ContentTypeEnum,
  CookiesEnum,
  cookiesValues,
  CTA_SEEK_INTERVAL_SECONDS,
  LangEnum,
  LocalStorageKeysEnum,
  VIDEO_PLAYER_CURRENT_TIME_THROTTLE_MS,
  VideoPlayerNameEnum,
  VideoReadyState,
} from "@/constants/constants";
import { useUserContext } from "@/context/UserProvider";
import { useClientTranslations } from "@/hooks/useClientTranslations";
import { useThrottleCallback } from "@/hooks/useThrottleCallback";
import { useSpecialAccess } from "@/hooks/useSpecialAccess";
import { MappedContentDetail, SignedUrlRes } from "@/types/consumption.types";
import {
  MappedEpisodeDetail,
  MappedMovieDetail,
} from "@/types/contentDetails.types";
import { seekParams, VideoPlayerContextType } from "@/types/videoPlayer.types";
import {
  captureError,
  checkIsHLS265Supported,
  checkIsWebOS,
  generateCryptoBasedUUID,
  getClientSideCookieValue,
  getLangDetailsFromApiKey,
  getNextEpisodeUrl,
  isAvoidIntersectingEvent,
  isMobileDevice,
  isTvEnv,
} from "@/utils/common.helpers";
import { Line, SrtParser } from "@/utils/SrtParser.helper";
import Hls, { ErrorDetails } from "hls.js";
import { useParams, useSearchParams } from "next/navigation";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getUAParser } from "@/utils/lazyUAParser";

// Module-level flag: set before navigating to next episode so cleanup effects can skip orientation unlock
let isNavigatingToNextEpisode = false;
export { isNavigatingToNextEpisode };

export const VideoPlayerContext = createContext<VideoPlayerContextType>({
  watchableItem: null,
  contentDetail: null,
  videoRef: { current: null },
  isPlaying: false,
  currentTime: 0,
  duration: 0,
  showControls: false,
  isLoading: false,
  isEnded: false,
  isLastEpisode: false,
  nextEpisodeUrl: "",
  freePreviewTagText: undefined,
  showControlsTemporarily: () => {},
  showControlsPermanently: () => {},
  hideControlsIfPlaying: () => {},
  handleTimeUpdate: () => {},
  handlePlay: () => {},
  handlePause: () => {},
  handleLoadedMetadata: () => {},
  seek: () => {},
  togglePlayPause: () => {},
  availableQualities: [],
  currentQuality: "",
  availableSubtitles: [],
  currentSubtitle: "",
  availablePlaybackSpeeds: [],
  currentPlaybackSpeed: 0,
  isFullscreen: false,
  subtitlesMap: new Map(),
  handleFullscreen: () => {},
  enterFullscreen: () => {},
  exitFullscreen: () => {},
  setControlsInteracting: () => {},
  markNavigatingToNextEpisode: () => {},
  handleSpeedChange: () => {},
  handleSubtitleChange: () => {},
  handleQualityChange: () => {},
});

export const VideoPlayerProvider = ({
  watchableItem,
  contentDetail,
  startPlayingAt,
  signedURLDetails,
  videoElementId,
  enableMuxAnalytics,
  children,
  onPlay,
  onPause,
  onTimeUpdate,
  onLoadedMetadata,
  onSeek,
  onQualityChange,
  onSubtitleChange,
  onSpeedChange,
  freePreviewTagText,
}: {
  contentDetail: MappedContentDetail;
  signedURLDetails: SignedUrlRes;
  autoPlay?: boolean;
  watchableItem: MappedEpisodeDetail | MappedMovieDetail;
  children: React.ReactNode;
  startPlayingAt: number;
  videoElementId: string;
  enableMuxAnalytics: boolean;
  onPlay: () => void;
  onPause: () => void;
  onTimeUpdate: (time: number) => void;
  onLoadedMetadata: () => void;
  onSeek: (time: number) => void;
  onQualityChange: (quality: string) => void;
  onSubtitleChange: (subtitle: string) => void;
  onSpeedChange: (speed: number) => void;
  freePreviewTagText?: string;
}) => {
  // TV: Hardcoded quality list
  const tvQualities = [
    { label: "4K", value: "4K", bitrate: 2160 },
    { label: "1080p", value: "1080p", bitrate: 1080 },
    { label: "720p", value: "720p", bitrate: 720 },
  ];

  const params = useParams();
  const searchParams = useSearchParams();

  const { user } = useUserContext();
  const { t } = useClientTranslations();

  // Get content ID for special access check
  const contentId = useMemo(() => {
    return "_id" in contentDetail ? contentDetail._id : contentDetail.id;
  }, [contentDetail]);

  // Check if this is special access content
  const { isSpecialAccessContent } = useSpecialAccess(contentId);

  const videoRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isEnded, setIsEnded] = useState(false);

  const [availableQualities, setAvailableQualities] = useState<
    Array<{ label: string; value: string; bitrate?: number }>
  >([]);
  const [currentQuality, setCurrentQuality] = useState<string>("auto");
  const [availableSubtitles, setAvailableSubtitles] = useState<
    Array<{ label: string; value: string }>
  >([]);
  const [currentSubtitle, setCurrentSubtitle] = useState<string>("off");
  const [availablePlaybackSpeeds] = useState<
    Array<{ label: { en: string; hi: string }; value: number }>
  >([
    { label: { en: "0.5x", hi: "0.5x" }, value: 0.5 },
    { label: { en: "0.75x", hi: "0.75x" }, value: 0.75 },
    { label: { en: "1x (Normal)", hi: "1x (नॉर्मल)" }, value: 1 },
    { label: { en: "1.25x", hi: "1.25x" }, value: 1.25 },
    { label: { en: "1.5x", hi: "1.5x" }, value: 1.5 },
  ]);
  const [currentPlaybackSpeed, setCurrentPlaybackSpeed] = useState<number>(1);
  const [subtitlesMap, setSubtitlesMap] = useState<Map<string, Array<Line>>>(
    new Map(),
  );

  const controlsTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const isMuxAnalyticsInitializedRef = useRef(false);
  const muxLoadedRef = useRef<any>(null);

  const windowReloadTriggerRef = useRef(false);

  // Auto quality switching refs for freeze detection
  const freezeCountRef = useRef<number>(0);
  const lastFreezeTimeRef = useRef<number>(0);
  const autoQualitySwitchCooldownRef = useRef<number>(0);
  const freezeCooldownMs = 10000; // 10 seconds cooldown between auto quality switches

  const throttledSetCurrentTime = useThrottleCallback(
    setCurrentTime,
    VIDEO_PLAYER_CURRENT_TIME_THROTTLE_MS,
  );

  // ===== AUTO QUALITY SWITCHING FOR FREEZE DETECTION =====
  const switchToLowerQuality = useCallback(() => {
    const hls = hlsRef.current;
    if (!hls || !hls.levels || hls.levels.length <= 1) {
      return;
    }
    const currentLevel = hls.currentLevel;
    // Determine quality order by comparing first and last levels
    const isAscendingOrder =
      hls.levels[0].height < hls.levels[hls.levels.length - 1].height;

    let nextLowerLevel: number;

    if (isAscendingOrder) {
      // Ascending order: higher index = higher quality, so go to lower index
      nextLowerLevel = currentLevel - 2;
    } else {
      // Descending order: higher index = lower quality, so go to higher index
      nextLowerLevel = currentLevel + 2;
    }

    // Check if there's a lower quality available
    if (nextLowerLevel >= 0 && nextLowerLevel < hls.levels.length) {
      hls.currentLevel = nextLowerLevel;

      // Update current quality state
      const newQuality = availableQualities.find(
        (q) => q.bitrate === hls.levels[nextLowerLevel].height,
      );

      if (newQuality) {
        setCurrentQuality(newQuality.value);
        onQualityChange(newQuality.value);
      }

      // Set cooldown to prevent rapid switching
      autoQualitySwitchCooldownRef.current = Date.now();
    }
  }, [availableQualities, onQualityChange]);

  const handleFreezeDetection = useCallback(() => {
    const now = Date.now();
    // Check cooldown period
    if (now - autoQualitySwitchCooldownRef.current < freezeCooldownMs) {
      return;
    }
    // Increment freeze count
    freezeCountRef.current++;
    lastFreezeTimeRef.current = now;

    // Switch quality after 1 consecutive freeze (reduced for faster recovery)
    if (freezeCountRef.current >= 1) {
      switchToLowerQuality();
      freezeCountRef.current = 0; // Reset counter
    }
  }, [switchToLowerQuality]);

  const resetFreezeCount = useCallback(() => {
    freezeCountRef.current = 0;
  }, []);

  // Simple seek recovery function
  const recoverFromSeekLoop = useCallback(() => {
    const video = videoRef.current;
    const hls = hlsRef.current;

    if (!video || !hls) return;

    const targetTime = video.currentTime;
    const wasPlaying = !video.paused;

    // RECOVERY MECHANISM: Pause-Seek-Play cycle to break video freeze
    video.pause();

    // Wait for pause to take effect before micro-seek
    setTimeout(() => {
      // Force a micro-seek to break the loop
      video.currentTime = targetTime + 0.01;

      // Wait for micro-seek to complete before resuming playback
      // Browser needs time to process the seek operation before we can safely play
      setTimeout(() => {
        if (wasPlaying) {
          video.play();
        }
      }, 100); // 100ms: Sufficient time for seek operation to complete
    }, 100); // 100ms: Sufficient time for pause operation to complete
  }, []);

  const nextEpisodeUrl = useMemo(() => {
    const { slug, dialect, contentType, lang } = params;
    const [showSlug, currEpisodeNo] = slug as Array<string>;
    const parsedEpisodeNo = parseInt(currEpisodeNo);
    const seasonIdParam = searchParams.get("seasonId");
    const currentSeasonId = seasonIdParam ? parseInt(seasonIdParam) : null;

    const nextEpisodeUrl = getNextEpisodeUrl(
      lang as LangEnum,
      dialect as string,
      contentType as string,
      showSlug,
      parsedEpisodeNo,
      currentSeasonId,
      contentDetail,
    );

    return nextEpisodeUrl;
  }, [contentDetail, watchableItem]);

  const isLastEpisode = useMemo(() => {
    return !nextEpisodeUrl;
  }, [nextEpisodeUrl]);

  const isHLS265PlayabckRef = useRef<boolean | null>(true);

  // reInitHls is used to reinitialize the HLS instance when hls 265 is not supported
  const [reInitHls, setReInitHls] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const initMuxAndHls = async () => {
      const isMobile = isMobileDevice();

      let shouldUseHLS265StorageValue = localStorage.getItem(
        LocalStorageKeysEnum.SHOULD_USE_HLS_265,
      );

      let shouldUseHLS265: boolean = false;

      if (!isMobile) {
        // for mobile devices, we don't use HLS265
        shouldUseHLS265 =
          shouldUseHLS265StorageValue === null
            ? null
            : JSON.parse(shouldUseHLS265StorageValue);
      }

      if (shouldUseHLS265 !== null) {
        isHLS265PlayabckRef.current = shouldUseHLS265;
      }

      const signedObject =
        shouldUseHLS265 === false
          ? signedURLDetails.signedObject
          : signedURLDetails.signedObjectH265;
      const signedCookies = signedObject ? signedObject.signedCookies : null;

      let src = signedObject
        ? signedObject.signedUrl
        : signedURLDetails.signedUrl;
      // for lg tv 4k, we need to use 1080p playlist
      if (isTvEnv() && checkIsWebOS() && window.innerHeight > 1080) {
        src = src.replace("playlist", "1080p");
      }

      const setQualityLevelInMuxAnalytics = () => {
        if (
          hlsRef.current?.loadLevel === -1 ||
          !isMuxAnalyticsInitializedRef.current ||
          !muxLoadedRef.current
        ) {
          return;
        }

        const qualityLevel =
          hlsRef.current?.levels[hlsRef.current?.loadLevel]?.height;

        muxLoadedRef.current.updateData(`#${videoElementId}`, {
          video_source_bitrate: qualityLevel,
        });
      };
      const getMuxUserId = () => {
        const userDataCookie = getClientSideCookieValue(
          cookiesValues[CookiesEnum.UserData],
        );

        const userId = (() => {
          try {
            return JSON.parse(decodeURIComponent(userDataCookie || "")).userId;
          } catch (e) {
            return undefined;
          }
        })();

        window.muxId =
          userId ||
          window.muxId ||
          "anonymous_mux_" + generateCryptoBasedUUID();
        return window.muxId;
      };

      const initMuxAnalytics = async (hls: Hls) => {
        if (
          !enableMuxAnalytics ||
          process.env.NEXT_PUBLIC_ENABLE_MUX_VIDEO_ANALYTICS !== "true"
        ) {
          return;
        }

        // Lazy load mux-embed only when needed
        if (!muxLoadedRef.current) {
          try {
            const muxModule = await import("mux-embed");
            muxLoadedRef.current = muxModule.default;
          } catch (error) {
            console.error("Failed to load mux-embed:", error);
            return;
          }
        }

        const mux = muxLoadedRef.current;
        const initTime = mux.utils.now();

        const uaParser = await getUAParser();
        const deviceModel = uaParser.getDevice().model;
        const deviceVendor = uaParser.getDevice().vendor;
        const os = uaParser.getOS();
        const osName = os.name;
        const osVersion = os.version;

        const appName: string =
          osName === "Tizen"
            ? AppNameEnum.SAMSUNG_TV_APP
            : checkIsWebOS()
              ? AppNameEnum.LG_TV_APP
              : AppNameEnum.STAGE_WEBSITE;

        const commonMuxProperties = {
          platform: {
            name: appName,
            product: deviceModel,
            manufacturer: deviceVendor,
            os: {
              family: osName,
              version: osVersion,
            },
          },
          data: {
            env_key: process.env.NEXT_PUBLIC_MUX_VIDEO_ANALYTICS_API_KEY,
            player_name:
              shouldUseHLS265 === false
                ? VideoPlayerNameEnum.HLS_JS
                : VideoPlayerNameEnum.HLS_JS_H265,
            player_init_time: initTime,
            video_title: watchableItem.title,
            video_id: watchableItem._id.toString(),
            video_source_url: src,
            video_source_duration: watchableItem.duration,
            video_duration: watchableItem.duration,
            video_stream_type: "hls",
            video_content_type:
              "episodes" in contentDetail
                ? ContentTypeEnum.SHOW
                : ContentTypeEnum.MOVIE,
            viewer_user_id: getMuxUserId(),
            custom_1: getMuxUserId(),
            custom_2:
              getClientSideCookieValue(cookiesValues[CookiesEnum.Status]) ??
              "not_found",
          },
        };

        try {
          mux.monitor(`#${videoElementId}`, {
            hlsjs: hls,
            Hls,
            ...commonMuxProperties,
            errorTranslator: (error: any) => {
              // Filter out 403 errors from Mux analytics - these are handled by HLS retry mechanism
              // player_error_context value is "url: https://d3e6qunn99eica.cloudfront.net/videos/show/vidshi-bahu/episodes/HLS-H265/videshi-bahu-ep4_1729356093160/audio/en/mp4a.40.2/seg-81.m4s\nresponse: 403, \nerror: Error: HTTP Error 403 \n"
              if (error.player_error_context?.includes("403")) {
                return false;
              }

              return error;
            },
          });
          setQualityLevelInMuxAnalytics();
          isMuxAnalyticsInitializedRef.current = true;
        } catch (e) {
          console.error("Error initializing mux analytics:", e);
        }
      };

      const initHLS = async () => {
        try {
          const Hls = (await import("hls.js")).default;

          if (Hls.isSupported()) {
            const hls = new Hls({
              capLevelToPlayerSize: true,
              startPosition: startPlayingAt,
              debug: false, // change to true to debug hls issues
              fragLoadingTimeOut: 30000, // fix for fragLoadTimeOut
              manifestLoadingTimeOut: 30000, // fix for manifestLoadTimeOut
              levelLoadingTimeOut: 30000, // fix for levelLoadTimeOut
              maxBufferLength: 20, // seconds
              maxMaxBufferLength: 30, // seconds
              backBufferLength: 30, // seconds

              xhrSetup: (xhr: XMLHttpRequest, url: string) => {
                // Add signed cookies as URL parameters for CloudFront
                const cookieParams = signedCookies
                  ? Object.entries(signedCookies)
                      .map(
                        ([key, value]) =>
                          `${encodeURIComponent(key.replace("CloudFront-", ""))}=${encodeURIComponent(value)}`,
                      )
                      .join("&")
                  : "";

                if (cookieParams) {
                  const separator = url.includes("?") ? "&" : "?";
                  const urlWithCookies = `${url}${separator}${cookieParams}`;
                  xhr.open("GET", urlWithCookies, true);
                }
              },
            });

            hls.loadSource(src);
            hls.attachMedia(video);
            hlsRef.current = hls;
            initMuxAnalytics(hls).catch((e) => {
              console.error("initMuxAnalytics failed:", e);
            });

            // Set up quality levels when manifest is loaded
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
              const isTV = isTvEnv();

              if (isTV) {
                // Get content supported qualities
                // Check if both content and TV supports  4K
                const hlsQualities = hls.levels.map((level) => level.height);

                const tvMaxResolution =
                  window.screen.height * window.devicePixelRatio;

                const supports4K =
                  hlsQualities.includes(2160) && tvMaxResolution >= 2160;

                const supportedTvQualities = tvQualities.filter((quality) => {
                  if (quality.bitrate === 2160) {
                    return supports4K;
                  }
                  return true;
                });
                const finalQualities = [
                  { label: t("common.autoQualityLabel"), value: "auto" },
                  ...supportedTvQualities,
                ];
                setAvailableQualities(finalQualities);
              } else {
                const _qualities = watchableItem.videoFormatDetail.map(
                  (format) => ({
                    label: format.label,
                    value: format.label,
                    bitrate: format.bitRate,
                  }),
                );

                // Multiply by device pixel ratio to get the actual pixel resolution
                // On high-DPI displays, the logical screen height differs from physical pixels
                const deviceMaxResolution =
                  window.screen.height * window.devicePixelRatio;

                let supportedQualities = _qualities.filter(
                  (quality) => quality.bitrate <= deviceMaxResolution,
                );

                const finalQualities = [
                  { label: t("common.autoQualityLabel"), value: "auto" },
                  ...supportedQualities,
                ];

                setAvailableQualities(finalQualities);
              }

              // Set initial quality to auto
              hls.currentLevel = -1;
              setCurrentQuality("auto");
              // Set up subtitle tracks - both embedded HLS and external tracks

              const subtitleTracks: { label: string; value: string }[] = [];

              if (watchableItem.subtitle) {
                subtitleTracks.push({ label: t("common.off"), value: "off" });
                Object.keys(watchableItem.subtitle).forEach(
                  (langKey: string) => {
                    if (watchableItem.subtitle?.[langKey]) {
                      const langDetails = getLangDetailsFromApiKey(langKey);
                      subtitleTracks.push({
                        label: langDetails?.label || langKey,
                        value: `${langKey}`,
                      });
                    }
                  },
                );

                setAvailableSubtitles(subtitleTracks);
                setCurrentSubtitle("off");
              }
            });

            hls.on(Hls.Events.ERROR, async (event, data) => {
              if (data.response?.code === 403) {
                if (!windowReloadTriggerRef.current) {
                  windowReloadTriggerRef.current = true;
                  window.location.reload();
                }
                return;
              }

              if (
                data.type === Hls.ErrorTypes.MEDIA_ERROR &&
                isHLS265PlayabckRef.current
              ) {
                let isMediaDecodingError = false;
                // Check if there's an error
                if (video && video.error) {
                  isMediaDecodingError =
                    video.error.code === MediaError.MEDIA_ERR_DECODE;
                }

                if (isHLS265PlayabckRef.current) {
                  const inCompatibleCodecsError =
                    data.details ===
                    ErrorDetails.MANIFEST_INCOMPATIBLE_CODECS_ERROR;

                  const isSupportedCodec = checkIsHLS265Supported().every;

                  const shoudlTriggerUseEffect =
                    isMediaDecodingError ||
                    inCompatibleCodecsError ||
                    !isSupportedCodec;

                  if (shoudlTriggerUseEffect) {
                    localStorage.setItem(
                      LocalStorageKeysEnum.SHOULD_USE_HLS_265,
                      "false",
                    );
                    setReInitHls(true);
                  }
                } else {
                  hls.recoverMediaError();
                }
              }
              if (data.fatal) {
                switch (data.type) {
                  case Hls.ErrorTypes.NETWORK_ERROR:
                    hls.startLoad();
                    break;
                  case Hls.ErrorTypes.MEDIA_ERROR:
                    hls.recoverMediaError();
                    break;
                  default:
                    hls.destroy();
                    break;
                }

                try {
                  await sendToSupabase({
                    userId: getMuxUserId(),
                    eventName: "hls_fatal_error",
                    eventValue: JSON.stringify({
                      ...data,
                      isHLS265Stream: shouldUseHLS265,
                      signedCookies,
                      contentId:
                        "id" in contentDetail
                          ? contentDetail.id
                          : contentDetail._id,
                      watchableItemId: watchableItem._id,
                      width: window.screen.width,
                      height: window.screen.height,
                      innerWidth: window.innerWidth,
                      innerHeight: window.innerHeight,
                      quality:
                        hls.levels[hls.loadLevel]?.height || currentQuality,
                      videoElementError: videoRef.current?.error
                        ? {
                            code: videoRef.current.error.code,
                            message: videoRef.current.error.message,
                          }
                        : null,
                      hlsMediaError: hls.media?.error
                        ? {
                            code: hls.media.error.code,
                            message: hls.media.error.message,
                          }
                        : null,
                    }),
                  });
                } catch (error) {
                  // do nothing
                }
              }
            });

            hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
              setQualityLevelInMuxAnalytics();
            });
          } else {
            video.src = src;
          }
        } catch (error) {
          video.src = src;
        }
      };

      initHLS();
    };

    // Helper function to check if video should show loading state
    const shouldShowLoading = (videoElement: HTMLVideoElement): boolean => {
      return (
        videoElement.paused ||
        videoElement.readyState < VideoReadyState.HAVE_FUTURE_DATA
      );
    };

    // Helper function to check if video is ready to play
    const isVideoReadyToPlay = (videoElement: HTMLVideoElement): boolean => {
      return (
        !videoElement.paused &&
        videoElement.readyState >= VideoReadyState.HAVE_FUTURE_DATA
      );
    };

    initMuxAndHls();

    // Video element event listeners
    const handleWaitingOrStalled = () => {
      // Only show loading if video is actually paused or not ready
      if (shouldShowLoading(video)) {
        setIsLoading(true);

        // Trigger freeze detection for automatic quality switching
        handleFreezeDetection();
      }
    };
    const handlePlaying = () => {
      setIsLoading(false);
      // Reset freeze count when video plays smoothly
      resetFreezeCount();
    };
    const handleLoadStart = () => setIsLoading(true);
    const handleCanPlay = () => {
      setIsLoading(false);
      // Reset freeze count when video can play
      resetFreezeCount();
    };
    const handleError = () => setIsLoading(false);
    const handleSeeking = () => setIsLoading(true);
    const handleSeeked = () => {
      // Completely event-driven solution: listen for when video is actually ready to play

      const hideLoadingOnce = () => {
        if (isVideoReadyToPlay(video)) {
          setIsLoading(false);
          cleanup();
        }
      };

      const cleanup = () => {
        video.removeEventListener("canplay", hideLoadingOnce);
        video.removeEventListener("progress", progressHandler);
        video.removeEventListener("playing", hideLoadingOnce);
        video.removeEventListener("canplaythrough", hideLoadingOnce);
      };

      // Listen for multiple events that indicate video is ready to play
      video.addEventListener("canplay", hideLoadingOnce);
      video.addEventListener("playing", hideLoadingOnce);
      video.addEventListener("canplaythrough", hideLoadingOnce);

      // Also listen for progress events as a fallback
      const progressHandler = () => {
        if (video.buffered.length > 0) {
          for (let i = 0; i < video.buffered.length; i++) {
            if (
              video.currentTime >= video.buffered.start(i) &&
              video.currentTime <= video.buffered.end(i)
            ) {
              hideLoadingOnce();
              break;
            }
          }
        }
      };

      video.addEventListener("progress", progressHandler);

      // Timeout-based recovery: if video doesn't become ready within 2 seconds, force recovery
      const recoveryTimeout = setTimeout(() => {
        // Use the existing seek loop recovery mechanism
        recoverFromSeekLoop();

        // Force loading state off after timeout
        setIsLoading(false);
        cleanup();
      }, 2000);

      // Clear timeout if video becomes ready
      const originalHideLoadingOnce = hideLoadingOnce;
      const enhancedHideLoadingOnce = () => {
        clearTimeout(recoveryTimeout);
        originalHideLoadingOnce();
      };

      // Replace event listeners with enhanced version
      video.removeEventListener("canplay", hideLoadingOnce);
      video.removeEventListener("playing", hideLoadingOnce);
      video.removeEventListener("canplaythrough", hideLoadingOnce);

      video.addEventListener("canplay", enhancedHideLoadingOnce);
      video.addEventListener("playing", enhancedHideLoadingOnce);
      video.addEventListener("canplaythrough", enhancedHideLoadingOnce);
    };
    const handleCanPlayThrough = () => setIsLoading(false);
    const handleProgress = () => {
      // Hide loading if we have data at current position
      if (video.buffered.length > 0) {
        for (let i = 0; i < video.buffered.length; i++) {
          if (
            video.currentTime >= video.buffered.start(i) &&
            video.currentTime <= video.buffered.end(i)
          ) {
            setIsLoading(false);
            break;
          }
        }
      }
    };
    const handleEnded = () => {
      setIsEnded(true);
      setIsPlaying(false);
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };

    video.addEventListener("waiting", handleWaitingOrStalled);
    video.addEventListener("playing", handlePlaying);
    video.addEventListener("loadstart", handleLoadStart);
    video.addEventListener("canplay", handleCanPlay);
    video.addEventListener("stalled", handleWaitingOrStalled);
    video.addEventListener("error", handleError);
    video.addEventListener("seeking", handleSeeking);
    video.addEventListener("seeked", handleSeeked);
    video.addEventListener("canplaythrough", handleCanPlayThrough);
    video.addEventListener("progress", handleProgress);
    video.addEventListener("ended", handleEnded);

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }

      if (isMuxAnalyticsInitializedRef.current && muxLoadedRef.current) {
        muxLoadedRef.current.destroyMonitor(`#${videoElementId}`);
        isMuxAnalyticsInitializedRef.current = false;
      }

      video.removeEventListener("waiting", handleWaitingOrStalled);
      video.removeEventListener("playing", handlePlaying);
      video.removeEventListener("loadstart", handleLoadStart);
      video.removeEventListener("canplay", handleCanPlay);
      video.removeEventListener("stalled", handleWaitingOrStalled);
      video.removeEventListener("error", handleError);
      video.removeEventListener("seeking", handleSeeking);
      video.removeEventListener("seeked", handleSeeked);
      video.removeEventListener("canplaythrough", handleCanPlayThrough);
      video.removeEventListener("progress", handleProgress);
      video.removeEventListener("ended", handleEnded);
    };
  }, [signedURLDetails, reInitHls]);

  const hideControlsAfterDelay = useCallback(() => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  }, []);

  const showControlsTemporarily = useCallback(() => {
    setShowControls(true);
    if (isPlaying) {
      hideControlsAfterDelay();
    }
  }, [isPlaying, hideControlsAfterDelay]);

  const showControlsPermanently = useCallback(() => {
    setShowControls(true);
  }, []);

  const handlePlay = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
      setIsEnded(false); // Reset ended state when playing
      onPlay();
      hideControlsAfterDelay();
    }
  }, [onPlay]);

  const handlePause = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
      onPause();
      setShowControls(true);
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    }
  }, [onPause]);

  const togglePlayPause = useCallback(() => {
    const isPaused = videoRef.current?.paused;
    if (isPaused) {
      handlePlay();
    } else {
      handlePause();
    }
  }, [handlePlay, handlePause]);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      const time = videoRef.current.currentTime;

      throttledSetCurrentTime(time);
      onTimeUpdate(time);
    }
  }, [onTimeUpdate, throttledSetCurrentTime]);

  // Helper function to check if time is within buffer
  const isTimeInBuffer = useCallback(
    (video: HTMLVideoElement, time: number): boolean => {
      if (video.buffered.length === 0) return false;

      for (let i = 0; i < video.buffered.length; i++) {
        if (time >= video.buffered.start(i) && time <= video.buffered.end(i)) {
          return true;
        }
      }
      return false;
    },
    [],
  );

  const seek = useCallback(
    (params: seekParams) => {
      // Prevent absolute seeking (from seekbar) if this is special access content
      // Allow relative seeking (from forward/rewind buttons)
      if (isSpecialAccessContent && "to" in params) {
        return;
      }

      if (videoRef.current) {
        const video = videoRef.current;
        const hls = hlsRef.current;

        let newTime = video.currentTime;
        if ("to" in params) {
          newTime = params.to;
        } else {
          newTime = video.currentTime + params.by;
        }

        // Ensure newTime is within valid bounds
        const duration = video.duration || 0;
        newTime = Math.max(0, Math.min(newTime, duration));

        // Preserve current quality level to prevent automatic quality reduction during seek
        const preservedLevel = hls?.currentLevel;

        // Check if seeking outside buffer while fragment is loading
        const isOutsideBuffer = !isTimeInBuffer(video, newTime);
        const isFragmentLoading = hls && hls.loadLevel !== undefined;

        if (isOutsideBuffer && isFragmentLoading) {
          // Cancel current fragment loading to prevent conflicts
          try {
            hls.stopLoad();

            // Force seek to the exact position first
            video.currentTime = newTime;

            // Wait a bit longer and restart loading
            setTimeout(() => {
              // Restore the preserved quality level before restarting load
              if (preservedLevel !== undefined && hls) {
                hls.currentLevel = preservedLevel;
              }
              hls.startLoad();

              // Additional recovery: force play if paused
              if (video.paused) {
                video.play().catch((err) => {
                  captureError("Auto-play failed after seek", err);
                });
              }
            }, 100);
          } catch (error) {
            captureError("Error canceling fragment load", error);
          }
        } else if (isOutsideBuffer) {
          // Even if no fragment is loading, if we're outside buffer

          // Force seek and restart loading
          video.currentTime = newTime;

          if (hls) {
            hls.stopLoad();
            setTimeout(() => {
              // Restore the preserved quality level before restarting load
              if (preservedLevel !== undefined) {
                hls.currentLevel = preservedLevel;
              }
              hls.startLoad();
            }, 50);
          }
        }

        video.currentTime = newTime;
        setCurrentTime(newTime);
        /**
        // Call throttledSetCurrentTime with newTime to prevent scheduled stale value from getting used in `useThrottleCallback`'s setTimeout
        // This fixes an issue where the seekbar temporarily shows the old position after seeking
        // @see https://stagedotin.slack.com/archives/C06NYMGLNGH/p1755675369972359 for more details of the issue
        */
        throttledSetCurrentTime(newTime);
        setIsEnded(false); // Reset ended state when seeking
        onSeek(newTime);
      }
    },
    [onSeek, isTimeInBuffer, isSpecialAccessContent],
  );

  const handleFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      setIsFullscreen(false);
    } else {
      // Use document.body for fullscreen instead of video-container to ensure UI elements like drawers remain accessible in fullscreen mode

      document.body.requestFullscreen();
      setIsFullscreen(true);
    }
  }, []);

  const enterFullscreen = useCallback(() => {
    document.body.requestFullscreen();
    setIsFullscreen(true);
  }, []);

  const exitFullscreen = useCallback(() => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  }, []);

  const isControlsInteractingRef = useRef(false);
  const setControlsInteracting = useCallback((interacting: boolean) => {
    isControlsInteractingRef.current = interacting;
  }, []);

  const markNavigatingToNextEpisode = useCallback(() => {
    isNavigatingToNextEpisode = true;
  }, []);

  const handleQualityChange = useCallback(
    (quality: { label: string; value: string; bitrate?: number }) => {
      const previousQuality = currentQuality;

      if (previousQuality === quality.value) {
        return;
      }

      const hls = hlsRef.current;

      if (hls && hls.levels) {
        if (quality.value === "auto") {
          hls.currentLevel = -1; // Auto quality
        } else {
          const levelIndex = hls.levels.findIndex(
            (level: any) => level.height == quality.bitrate,
          );
          if (levelIndex !== -1) {
            hls.currentLevel = levelIndex;
          }
        }
      }

      setIsLoading(true);
      setCurrentQuality(quality.value);
      onQualityChange(quality.value);
    },

    [onQualityChange, user.userId, currentSubtitle, currentQuality],
  );

  const handleSubtitleChange = useCallback(
    (subtitle: { label: string; value: string }) => {
      const video = videoRef.current;

      if (subtitle.value === "off") {
        // Turn off all subtitles
        if (video) {
          for (const track of Array.from(video.textTracks)) {
            track.mode = "hidden";
          }
        }
      } else {
        // Handle external subtitle tracks
        const lang = subtitle.value;

        // Enable the specific external track
        if (video) {
          let trackFound = false;
          for (const track of Array.from(video.textTracks)) {
            if (track.language === lang) {
              // Force track mode change and verify it sticks
              track.mode = "showing";

              trackFound = true;
            } else {
              track.mode = "hidden";
            }
          }

          if (!trackFound) {
            // this is necessary as we are using srt file for subtitles and native video tag does not support srt files
            loadSubtitleManually(lang);
          }
        }
      }

      setCurrentSubtitle(subtitle.value);
      onSubtitleChange(subtitle.value);
    },
    [currentQuality, currentSubtitle, onSubtitleChange],
  );

  const handleSpeedChange = useCallback(
    (value: number) => {
      const video = videoRef.current;

      if (video) {
        video.playbackRate = value;
        setCurrentPlaybackSpeed(value);
        onSpeedChange(value);
      }
    },
    [onSpeedChange],
  );

  // Manual subtitle loader that fetches and converts SRT to VTT if needed
  const loadSubtitleManually = useCallback(async (lang: string) => {
    const video = videoRef.current;
    if (!video) return;

    const url = `${process.env.NEXT_PUBLIC_CDN_ROOT_URL}/episode/srt/${watchableItem.subtitle?.[lang]}`;
    try {
      const response = await fetch(url);
      const content = await response.text();

      const parser = new SrtParser();
      const parsedSubtitles = parser.fromSrt(content);
      setSubtitlesMap((prev) => new Map(prev).set(lang, parsedSubtitles));
    } catch (error) {
      console.error("Error loading subtitle", error);
      captureError(error, {
        subtitle: lang,
        watchableItem: watchableItem,
        url: url,
      });
    }
  }, []);

  const hideControlsIfPlaying = useCallback(() => {
    if (isPlaying) {
      hideControlsAfterDelay();
    }
  }, [isPlaying]);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      onLoadedMetadata();
    }
  }, [onLoadedMetadata]);

  useEffect(() => {
    const shouldPreventSpatialNavigationListeners = (e: KeyboardEvent) => {
      return (
        e.key === "ArrowUp" ||
        e.keyCode === 38 ||
        e.key === "ArrowDown" ||
        e.keyCode === 40 ||
        e.key === "ArrowLeft" ||
        e.keyCode === 37 ||
        e.key === "ArrowRight" ||
        e.keyCode === 39 ||
        e.key === "Enter" ||
        e.keyCode === 13 ||
        e.key === "Escape" ||
        e.keyCode === 27 ||
        e.key === " " ||
        e.keyCode === 32
      );
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (showControls) {
        showControlsTemporarily();
        if (isTvEnv()) {
          return;
        }
      }

      if (isAvoidIntersectingEvent(e.key)) {
        if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
          showControlsTemporarily();
        }
        return;
      }

      if (shouldPreventSpatialNavigationListeners(e)) {
        // This prevents spatial navigation listeners from being triggered (when controls are not visible)
        // while the user is using the keyboard to control the video player, since the library attaches its listeners to the window.
        e.stopPropagation();
      }

      if (e.key === "ArrowLeft" || e.keyCode === 37) {
        seek({ by: -CTA_SEEK_INTERVAL_SECONDS });
      } else if (e.key === "ArrowRight" || e.keyCode === 39) {
        seek({ by: CTA_SEEK_INTERVAL_SECONDS });
      } else if (e.key === "Enter" || e.keyCode === 13) {
        showControlsTemporarily();
        togglePlayPause();
      } else if (e.key === "Escape" || e.keyCode === 27) {
        setShowControls(false);
      } else if (
        e.key === "ArrowUp" ||
        e.keyCode === 38 ||
        e.key === "ArrowDown" ||
        e.keyCode === 40
      ) {
        showControlsTemporarily();
      } else if (e.key === " " || e.keyCode === 32) {
        togglePlayPause();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [
    showControls,
    togglePlayPause,
    seek,
    showControlsTemporarily,
    isSpecialAccessContent,
  ]);

  return (
    <VideoPlayerContext.Provider
      value={{
        watchableItem,
        contentDetail,
        videoRef,
        isPlaying,
        currentTime,
        duration,
        showControls,
        isLoading,
        isEnded,
        isLastEpisode,
        nextEpisodeUrl,
        freePreviewTagText,
        availableQualities,
        currentQuality,
        availableSubtitles,
        currentSubtitle,
        availablePlaybackSpeeds,
        currentPlaybackSpeed,
        isFullscreen,
        showControlsTemporarily,
        showControlsPermanently,
        hideControlsIfPlaying,
        handleTimeUpdate,
        handlePlay,
        handlePause,
        handleLoadedMetadata,
        seek,
        togglePlayPause,
        handleFullscreen,
        enterFullscreen,
        exitFullscreen,
        setControlsInteracting,
        markNavigatingToNextEpisode,
        handleSpeedChange,
        handleSubtitleChange,
        handleQualityChange,
        subtitlesMap,
      }}
    >
      {children}
    </VideoPlayerContext.Provider>
  );
};

export const useVideoPlayerContext = () => {
  return useContext(VideoPlayerContext);
};

const sendToSupabase = async ({
  userId,
  eventName,
  eventValue,
}: {
  userId: string;
  eventName: string;
  eventValue: string;
}) => {
  try {
    const response = await fetch("/api/send-to-supabase", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        pool_number: -1,
        user_id: userId,
        event_name: eventName,
        event_value: eventValue,
      }),
    });
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error sending to Supabase:", error);
    throw error;
  }
};
